import React, { Suspense, useRef, useState } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import {
    OrbitControls, useGLTF, Environment,
    Center, useProgress, Html, Grid, GizmoHelper, GizmoViewport,
} from "@react-three/drei";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { BASE } from "../../services/api";

function resolveUrl(url) {
    if (!url) return "";
    return url.startsWith("http") ? url : `${BASE}${url}`;
}

// ── Loading overlay ───────────────────────────────────────────────────────────
function Loader() {
    const { progress } = useProgress();
    return (
        <Html center>
            <div className="flex flex-col items-center gap-3">
                <div className="relative w-14 h-14">
                    <svg className="w-14 h-14 -rotate-90" viewBox="0 0 56 56">
                        <circle cx="28" cy="28" r="24" fill="none" stroke="#ffffff10" strokeWidth="3" />
                        <circle cx="28" cy="28" r="24" fill="none" stroke="#0099ff" strokeWidth="3"
                            strokeDasharray={`${2 * Math.PI * 24}`}
                            strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
                            strokeLinecap="round"
                            style={{ transition: "stroke-dashoffset 0.3s ease" }}
                        />
                    </svg>
                    <span className="absolute inset-0 flex items-center justify-center font-mono text-xs text-white">
                        {Math.round(progress)}%
                    </span>
                </div>
                <span className="font-mono text-[10px] text-zinc-500 uppercase tracking-wider">Načítavam model…</span>
            </div>
        </Html>
    );
}

// ── GLB model ─────────────────────────────────────────────────────────────────
function GLBModel({ url, autoRotate }) {
    const { scene } = useGLTF(url);
    const ref = useRef();
    useFrame((_, delta) => {
        if (autoRotate && ref.current) ref.current.rotation.y += delta * 0.3;
    });
    return (
        <Center ref={ref}>
            <primitive object={scene} />
        </Center>
    );
}

// ── OBJ model ─────────────────────────────────────────────────────────────────
function OBJModel({ url, autoRotate }) {
    const obj = useLoader(OBJLoader, url);
    const ref = useRef();
    useFrame((_, delta) => {
        if (autoRotate && ref.current) ref.current.rotation.y += delta * 0.3;
    });
    return (
        <Center ref={ref}>
            <primitive object={obj} />
        </Center>
    );
}

function Model({ url, autoRotate }) {
    const ext = url.split(".").pop().toLowerCase();
    if (ext === "obj") return <OBJModel url={url} autoRotate={autoRotate} />;
    return <GLBModel url={url} autoRotate={autoRotate} />;
}

// ── Scene ─────────────────────────────────────────────────────────────────────
function Scene({ url, autoRotate, showGrid, showGizmo }) {
    return (
        <>
            <color attach="background" args={["#0a0a0f"]} />
            <fog attach="fog" args={["#0a0a0f", 30, 80]} />

            <ambientLight intensity={0.4} />
            <directionalLight position={[5, 8, 5]}  intensity={1.2} castShadow />
            <directionalLight position={[-5, 3, -3]} intensity={0.4} color="#4488ff" />
            <pointLight position={[0, 6, 0]} intensity={0.6} color="#0099ff" />

            <Environment preset="night" />

            <Suspense fallback={<Loader />}>
                <Model url={url} autoRotate={autoRotate} />
            </Suspense>

            {showGrid && (
                <Grid
                    position={[0, -0.01, 0]}
                    args={[30, 30]}
                    cellSize={1}
                    cellThickness={0.5}
                    cellColor="#1a1a2e"
                    sectionSize={5}
                    sectionThickness={1}
                    sectionColor="#0099ff22"
                    fadeDistance={25}
                    fadeStrength={1}
                    infiniteGrid
                />
            )}

            <OrbitControls
                makeDefault
                enableDamping
                dampingFactor={0.05}
                minDistance={1}
                maxDistance={50}
                autoRotate={false}
            />

            {showGizmo && (
                <GizmoHelper alignment="bottom-right" margin={[60, 60]}>
                    <GizmoViewport
                        axisColors={["#ff4444", "#44ff44", "#4488ff"]}
                        labelColor="white"
                    />
                </GizmoHelper>
            )}
        </>
    );
}

// ── Public component ──────────────────────────────────────────────────────────
export default function ModelViewer({ fileUrl, height = "100%", showControls = true }) {
    const [autoRotate, setAutoRotate] = useState(false);
    const [showGrid,   setShowGrid]   = useState(true);
    const [showGizmo,  setShowGizmo]  = useState(true);

    const url = resolveUrl(fileUrl);

    if (!url) {
        return (
            <div className="flex items-center justify-center h-full bg-nova-black/50 rounded-xl border border-white/8 text-zinc-600 font-mono text-sm">
                Žiadny model
            </div>
        );
    }

    return (
        <div className="relative w-full" style={{ height }}>
            <Canvas
                shadows
                camera={{ position: [4, 3, 6], fov: 45, near: 0.1, far: 200 }}
                style={{ borderRadius: "inherit" }}
            >
                <Scene url={url} autoRotate={autoRotate} showGrid={showGrid} showGizmo={showGizmo} />
            </Canvas>

            {showControls && (
                <div className="absolute bottom-3 left-3 flex gap-1.5 z-10">
                    <CtrlBtn active={autoRotate} onClick={() => setAutoRotate(v => !v)} title="Auto-rotácia">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                            <path d="M3 3v5h5" />
                        </svg>
                    </CtrlBtn>
                    <CtrlBtn active={showGrid} onClick={() => setShowGrid(v => !v)} title="Mriežka">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
                            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
                            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
                        </svg>
                    </CtrlBtn>
                    <CtrlBtn active={showGizmo} onClick={() => setShowGizmo(v => !v)} title="Osi">
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M12 2v20M2 12h20M5 5l14 14M19 5 5 19"/>
                        </svg>
                    </CtrlBtn>
                </div>
            )}

            {/* hint */}
            <div className="absolute top-3 left-3 font-mono text-[9px] text-zinc-600 pointer-events-none select-none">
                Drag → otočiť · Scroll → zoom · Right drag → posunúť
            </div>
        </div>
    );
}

function CtrlBtn({ active, onClick, title, children }) {
    return (
        <button
            onClick={onClick}
            title={title}
            className={`p-2 rounded-lg border backdrop-blur-sm transition-all ${
                active
                    ? "bg-nova-cyan/20 border-nova-cyan/40 text-nova-cyan"
                    : "bg-nova-black/60 border-white/10 text-zinc-500 hover:text-white hover:border-white/25"
            }`}
        >
            {children}
        </button>
    );
}
